import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as transportActions from '../../actions/transport';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import TransportTimeline from './TransportTimeline';
import { TRANSPORT_STATUS_COLORS } from '../../constants/constants';
import { TransportDetail as TransportDetailType } from '../../reducers/transport';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

const ch: React.CSSProperties = {
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2a5a8a 100%)',
    padding: '10px 16px', borderRadius: '8px 8px 0 0',
    fontSize: 12, fontWeight: 600, color: '#fff', textTransform: 'uppercase',
    letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 8,
};

const cb: React.CSSProperties = {
    background: '#fff', border: '1px solid #d0dbe8', borderTop: 'none',
    borderRadius: '0 0 8px 8px', padding: 14,
};

const infoLabel: React.CSSProperties = { fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.3px' };
const infoValue: React.CSSProperties = { fontSize: 13, color: '#0f172a', fontWeight: 500, marginTop: 2 };

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatDateOnly = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

interface Props {
    currentTransport: TransportDetailType | null;
    loading: boolean;
    actions: any;
    history: any;
    match: any;
}

const TransportDetail: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const transportId = parseInt(props.match.params.transportId);
    const [confirmingItemId, setConfirmingItemId] = useState<number | null>(null);
    const [deliveryNotes, setDeliveryNotes] = useState('');
    const [newCommentText, setNewCommentText] = useState('');

    useEffect(() => { if (transportId) props.actions.fetchTransport(transportId); }, [transportId]);

    const transport = props.currentTransport;

    // Group items by project — must be before early return (hooks can't be conditional)
    const deliveryGroups = useMemo(() => {
        const items = transport?.items || [];
        const groups: Record<string, { project: any; client: any; measureLocation: string | null; items: any[] }> = {};
        items.forEach((item: any) => {
            const projectId = item.project?.id || 'no-project';
            const key = `${projectId}-${item.client?.id || 'no-client'}`;
            if (!groups[key]) {
                groups[key] = {
                    project: item.project,
                    client: item.client,
                    measureLocation: item.measureLocation,
                    items: [],
                };
            }
            groups[key].items.push(item);
        });
        return Object.values(groups);
    }, [transport?.items]);

    if (!transport) return <div className="clp-page" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></div>;

    const statusColors = TRANSPORT_STATUS_COLORS[transport.status] || { color: '#94a3b8', bg: '#f1f5f9' };
    const confirmedCount = (transport.items || []).filter(i => i.deliveryConfirmedAt).length;
    const totalItems = (transport.items || []).length;

    const handleFinishTransport = async () => {
        const result = await props.actions.finishTransport(transportId);
        if (result?.error) {
            toast.error(result.error === 'already_delivered'
                ? fm('transport.detail.toast.alreadyDelivered', 'Transporti është përfunduar tashmë')
                : fm('transport.form.toast.somethingWrong', 'Diçka shkoi gabim'));
            return;
        }
        toast.success(fm('transport.detail.toast.finished', 'Transporti u përfundua'));
        props.actions.fetchTransport(transportId);
    };

    const handleConfirmDelivery = async (itemId: number) => {
        await props.actions.confirmItemDelivery(transportId, itemId, deliveryNotes);
        setConfirmingItemId(null); setDeliveryNotes('');
        props.actions.fetchTransport(transportId);
        toast.success(fm('transport.detail.toast.deliveryConfirmed', 'Dorëzimi u konfirmua'));
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) return;
        await props.actions.addTransportComment(transportId, newCommentText.trim());
        setNewCommentText('');
        props.actions.fetchTransport(transportId);
    };

    const parseCoords = (loc: string | null) => {
        if (!loc) return null;
        const parts = loc.split(',').map(s => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
        return null;
    };

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={fm('transport.detail.pageTitle', 'Transport #{id}', { id: transport.id })}
                subtitle={fm('transport.detail.createdBy', 'Krijuar nga {name} — {date}', { name: transport.createdBy?.name || '-', date: formatDate(transport.createdAt) })}
                actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: statusColors.color, background: statusColors.bg }}>
                            {transport.statusLabel}
                        </span>
                        {transport.status === 1 && (
                            <button onClick={handleFinishTransport}
                                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(34,197,94,0.2)', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                                <FormattedMessage id="transport.detail.finishButton" defaultMessage="Përfundo transportin" />
                            </button>
                        )}
                    </div>
                }
            />

            <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Info card */}
                <div>
                    <div style={ch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                        <FormattedMessage id="transport.without.detailsHeader" defaultMessage="Detajet" />
                    </div>
                    <div style={cb}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                            <div><div style={infoLabel}><FormattedMessage id="transport.col.driver" defaultMessage="Shoferi" /></div><div style={infoValue}>{transport.driver?.name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}><FormattedMessage id="transport.driver.unassigned" defaultMessage="Pa caktuar" /></span>}</div></div>
                            <div><div style={infoLabel}><FormattedMessage id="transport.col.departure" defaultMessage="Nisja" /></div><div style={infoValue}>{formatDate(transport.departureDate)}</div></div>
                            <div><div style={infoLabel}><FormattedMessage id="transport.col.arrival" defaultMessage="Arritja" /></div><div style={infoValue}>{transport.arrivalDate ? formatDate(transport.arrivalDate) : <span style={{ color: '#94a3b8' }}><FormattedMessage id="transport.detail.stillInTransit" defaultMessage="Ende në transport" /></span>}</div></div>
                            <div><div style={infoLabel}><FormattedMessage id="transport.col.duration" defaultMessage="Kohëzgjatja" /></div><div style={infoValue}>{transport.duration || '-'}</div></div>
                        </div>
                        {transport.notes && (
                            <div style={{ marginTop: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 6, fontSize: 12, color: '#475569', borderLeft: '3px solid #2a5a8a' }}>
                                {transport.notes}
                            </div>
                        )}
                    </div>
                </div>

                {/* Delivery cards — grouped by project */}
                {deliveryGroups.map((group, idx) => {
                    const coords = parseCoords(group.measureLocation);
                    const googleMapsUrl = coords ? `https://www.google.com/maps/dir/?api=1&destination=${coords[0]},${coords[1]}` : null;
                    const groupProducts = group.items.reduce((sum: number, item: any) => sum + (item.warehouseRecord?.products?.length || 0), 0);

                    return (
                        <div key={idx}>
                            <div className="t-card-header">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                {deliveryGroups.length > 1
                                    ? fm('transport.form.deliveryN', 'Dorëzimi {n}', { n: idx + 1 })
                                    : fm('transport.form.delivery', 'Dorëzimi')}
                                <span className="t-card-header-badge">{fm('transport.card.productsCount', '{count} produkte', { count: groupProducts })}</span>
                            </div>
                            <div className="t-card-body">
                                <div className="t-delivery-info">
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                        <div className="t-delivery-value">{group.client?.firstName || group.client?.name || '-'} {group.client?.lastName || ''}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="transport.form.phone" defaultMessage="Telefoni" /></div>
                                        <div className="t-delivery-value">{group.client?.phone || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="transport.form.location" defaultMessage="Lokacioni" /></div>
                                        <div className="t-delivery-value">
                                            {googleMapsUrl ? (
                                                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                    <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg><FormattedMessage id="transport.form.googleMaps" defaultMessage="Google Maps" />
                                                </a>
                                            ) : <span className="t-delivery-value-empty"><FormattedMessage id="transport.form.noLocation" defaultMessage="Pa lokacion" /></span>}
                                        </div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="transport.col.city" defaultMessage="Qyteti" /></div>
                                        <div className="t-delivery-value">{group.client?.city || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="transport.col.country" defaultMessage="Shteti" /></div>
                                        <div className="t-delivery-value">{group.client?.country || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                        <div className="t-delivery-value">{group.project?.name || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                </div>

                                <div className="t-products-section">
                                    {group.items.map((item: any) => (
                                        <div key={item.id} style={{ marginBottom: 10 }}>
                                            <div className="t-wr-header">
                                                <span className="t-wr-label">{item.warehouseRecord?.label || `WR-${item.id}`}</span>
                                                <span className="t-wr-badge">{fm('transport.card.productsCount', '{count} produkte', { count: item.warehouseRecord?.products?.length || 0 })}</span>
                                                {item.deliveryConfirmedAt && (
                                                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#059669', fontWeight: 600 }}>
                                                        {fm('transport.detail.delivered', 'Dorëzuar {date}', { date: formatDate(item.deliveryConfirmedAt) })}
                                                    </span>
                                                )}
                                            </div>
                                            {(item.warehouseRecord?.products || []).length > 0 ? (
                                                item.warehouseRecord.products.map((p: any, pIdx: number) => (
                                                    <div key={pIdx} className="t-product-row">
                                                        <span className="t-product-name">{p.productName || p.articleName || fm('transport.form.unnamed', 'Pa emër')}</span>
                                                        {p.type && <span className="t-product-type">{p.type}</span>}
                                                        <span className="t-product-qty">{p.quantity ? `${p.quantity} ${p.unit || ''}`.trim() : '-'}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="t-product-empty"><FormattedMessage id="transport.form.noProducts" defaultMessage="Pa produkte" /></div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Bottom row: Comments + Timeline */}
                <div style={{ display: 'flex', gap: 12 }}>
                    {/* Comments */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...ch, justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                <FormattedMessage id="transport.form.comments" defaultMessage="Komentet" />
                                {transport.comments?.length > 0 && <span style={{ opacity: 0.7, fontWeight: 400 }}>({transport.comments.length})</span>}
                            </div>
                        </div>
                        <div style={cb}>
                            {(transport.comments || []).map((c: any) => (
                                <div key={c.id} style={{
                                    padding: '8px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 6,
                                    borderLeft: '3px solid #2a5a8a',
                                }}>
                                    <div style={{ fontSize: 13, color: '#0f172a' }}><MentionText text={c.text} /></div>
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                                        {c.user?.name || fm('transport.detail.unknownUser', 'I panjohur')} — {formatDate(c.createdAt)}
                                    </div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 8, marginTop: (transport.comments || []).length > 0 ? 6 : 0 }}>
                                <MentionInput
                                    multiline
                                    placeholder={fm('transport.form.commentPlaceholder', 'Shkruaj një koment...')}
                                    value={newCommentText}
                                    onChange={(next) => setNewCommentText(next)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                                    style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid #d0dbe8', fontSize: 12, outline: 'none', minHeight: 36, resize: 'vertical' }}
                                />
                                <button onClick={handleAddComment} style={{
                                    padding: '6px 12px', borderRadius: 4, border: 'none', alignSelf: 'flex-end',
                                    background: '#1e3a5f', color: '#fff', fontSize: 11, cursor: 'pointer',
                                }}><FormattedMessage id="transport.form.addComment" defaultMessage="Shto" /></button>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TransportTimeline entries={transport.timeline || []} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentTransport: state.transport.currentTransport,
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(transportActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TransportDetail) as any) as any;
